import { useMemo } from "react";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { z } from "zod";
import {
  Box,
  CategoricalBarChart,
  ChartLegend,
  Text,
  WidgetInteractive,
  WidgetEmpty,
  WidgetSkeleton,
  truncateLabel,
  useCategoricalColors,
  useCategoryRowBudget,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { UNASSIGNED_DIMENSION_KEY, groupBySchema } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import {
  useShapedSlices,
  sliceDrilldownIds,
  AXES_DISPLAY_FIELDS,
  MISSING_FIELD_DESCRIPTION,
  MISSING_FIELD_TITLE,
  OTHER_SLICE_ID,
  SHOW_UNASSIGNED_SETTING_FIELD,
  SLICE_SHAPE_FIELDS,
  SLICE_SHOW_OPTIONS,
  groupBySettingField,
  showUnassignedSchema,
} from "#app/widgets/dashboards/use-dimension-slices";
import { unassignedDrillable } from "#app/widgets/dashboards/use-widget-drilldown-issues";
import {
  MEASURE_SETTING_FIELD,
  calculateSchema,
  compareFieldsSchema,
  measureFieldId,
  measureSchema,
  measureSettingFields,
  withLegacyDataKeys,
  type MeasureFormat,
} from "#app/widgets/dashboards/widget-measure";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { workloadTitle } from "../shared/derived-titles";
import { WIDGET_ACCENT } from "../shared/widget-accents";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * `dimension-bar` — projects grouped by any dimension (stage / type / team /
 * assignee / follow up / a custom field) as horizontal bars against a shared
 * value axis — the count per group, or a field aggregate per group under a
 * field Measure (`widget-measure.ts`). The bars run
 * in `rows` orientation so the category labels stay horizontal in a left
 * gutter: a stage or an assignee name does not survive being rotated onto a
 * vertical axis. Bars wear the shared slice assignment — a stage or type keeps
 * its own color, and dimensions whose members own no color (assignee above all)
 * take one hue rather than a rainbow that would encode nothing. Reads the same
 * `useShapedSlices` Show / Sort plumbing as the pie and battery cards.
 *
 * The folded tail is a CAPTION, never a bar: on a long-tailed dimension the
 * remainder outweighs every named category, so charting it stretches the axis
 * to the remainder and squeezes every named bar into the first third of the
 * plot. Its projects still count toward the shares the bars report.
 *
 * Compare with fields draw as GROUPED SERIES — one bar per field inside each
 * category, each field in its own hue with a legend — so Contract value and
 * Deposit collected sit side by side per stage. The tail and the shares then
 * follow the Measure field.
 *
 * A card and its full-screen preview get their own category budgets: a tile
 * holds a handful of readable bars, and the dialog has room for the tail behind
 * them. `expandedShow` is that second budget, so opening a card widens it
 * instead of showing the same short head at four times the size.
 */
export const dimensionBarConfigSchema = withLegacyDataKeys(
  z.looseObject({
    measure: measureSchema,
    calculate: calculateSchema,
    compareFields: compareFieldsSchema,
    groupBy: groupBySchema.default("stage"),
    showUnassigned: showUnassignedSchema,
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    expandedShow: z.enum(["top5", "top8", "top12", "all"]).default("top12"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    dateField: z.enum(["creationDate", "lastUpdateDate"]).optional(),
    filters: widgetFiltersSchema.nullish(),
  }),
  { compare: true },
);

export type DimensionBarConfig = z.infer<typeof dimensionBarConfigSchema>;

/** A value and its share of the card's whole — the one format the bars, the
 *  hover card and the folded-tail caption all read. The share is of the WHOLE,
 *  tail included: a member's 14% has to mean 14% of the work, not 14% of the
 *  bars that happened to fit. A share of an average or an extreme says nothing,
 *  so only a count or a sum carries one. */
const shareLabel = (value: number, total: number, format: MeasureFormat) => {
  const figure = format.compact(value);
  const fn = format.measure?.fn ?? "sum";
  if (fn !== "sum") return figure;
  return `${figure} · ${formatShare(value, total)}`;
};

/** One bar per category: the first field's figure under `value` and, under
 *  several fields, every field's under its `seriesKey`. */
type BarRow = {
  id: string;
  label: string;
  value: number;
  count: number;
  valueLabel: string;
  [series: `v${number}`]: number | undefined;
};
const seriesKey = (index: number): `v${number}` => `v${index}`;

function DimensionBarRender({ config, h, expanded }: WidgetRenderContext<DimensionBarConfig>) {
  // Older persisted Team Overview cards predate `dateField`; keep those cards on
  // the activity-oriented update window without forcing a layout migration.
  const dateField =
    config.dateField === "lastUpdateDate" || config.widgetKey === "team-overview.workload"
      ? "lastUpdateDate"
      : "creationDate";
  const { data, slices: rows, sliceColors, dimension, measure, measures, pending, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    measure: config.measure,
    calculate: config.calculate,
    compareFields: config.compareFields,
    showUnassigned: config.showUnassigned,
    sort: config.sort,
    // The full-screen preview carries its own budget: the tile is sized for a
    // readable handful, the dialog for as much of the tail as it can hold.
    show: expanded ? config.expandedShow : config.show,
    filters: config.filters,
    dateField,
    fallbackToken: WIDGET_ACCENT.dimensionBar,
  });
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  // Click-through: a bar press opens this card's Data drawer on the bar's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  // Show is a ceiling, not a promise: a short tile cannot name twelve groups,
  // and a chart handed more rows than it is tall stacks the labels on top of
  // each other. The rendered height decides how many bars survive.
  const { ref, budget } = useCategoryRowBudget();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);
  // Under several fields the SERIES are what hue tells apart, so each field
  // takes a ramp hue; a lone series leaves the hue to the categories.
  const several = measures.length > 1;
  const seriesColors = useCategoricalColors(
    useMemo(() => (several ? measures.map((format) => ({ label: format.label })) : []), [several, measures]),
  );

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="bars" />;
  }
  if (rows.length === 0) {
    return pending ? (
      <WidgetEmpty title={MISSING_FIELD_TITLE} description={MISSING_FIELD_DESCRIPTION} />
    ) : (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const onItemClick =
    drill && instance && dimension
      ? (item: { id: string }) => {
          const row = rows.find((candidate) => candidate.id === item.id);
          if (!row) return;
          // The no-value bar drills only where the filter can say "has none".
          if (row.id === UNASSIGNED_DIMENSION_KEY && !unassignedDrillable(dimension)) return;
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension,
            ids: sliceDrilldownIds(row, data, rows),
            label: row.label,
          });
        }
      : undefined;

  // `sliceColors.tokens` is index-aligned with `rows`, so every bar is built
  // over that set and the tail is split out of the BUILT rows. Under several
  // fields each row also carries every field's figure under `v<i>`.
  const items: BarRow[] = rows.map((row) => ({
    id: row.id,
    label: row.label,
    value: row.total,
    count: row.count ?? row.total,
    valueLabel: shareLabel(row.total, total, measure),
    ...Object.fromEntries(measures.map((_, index) => [seriesKey(index), row.values?.[index] ?? 0])),
  }));
  const series = several
    ? measures.map((_, index) => ({ key: seriesKey(index), colorToken: seriesColors.tokens[index] }))
    : [{ key: "value" }];
  // A short card has no room for tick labels, so the axes and the grid drop out
  // and the bars carry the comparison alone.
  const compact = h < 4;
  const showLegend = several && !compact;
  const ranked = items.filter((item) => item.id !== OTHER_SLICE_ID);
  const tail = items.find((item) => item.id === OTHER_SLICE_ID);
  const bars = budget === null ? ranked : ranked.slice(0, budget);
  // The caption covers EVERYTHING with no bar — the tail the Show setting
  // folded, plus whatever the tile was too short to draw. Counting only the
  // folded tail would leave the dropped rows silently missing from a card whose
  // shares are supposed to sum to the whole.
  const hiddenValue =
    (tail?.value ?? 0) + ranked.slice(bars.length).reduce((sum, item) => sum + item.value, 0);
  const folded = data.length - bars.length;
  // Adding hidden averages or extremes produces no figure; the caption then
  // counts the folded groups and stops.
  const additive = !measure.measure || measure.measure.fn === "sum";

  return (
    <Box direction="column" fill="flex" gap="2xs">
      <WidgetInteractive fill="flex" ref={ref}>
        <CategoricalBarChart
          // `shapeSlices` always appends the tail last, so the head of the token
          // set stays index-aligned with the bars once the tail is filtered out.
          data={bars}
          categoryKey="label"
          series={series}
          orientation="rows"
          categoryColorTokens={several ? undefined : sliceColors.tokens.slice(0, bars.length)}
          categoryAxisWidth={compact ? 72 : 128}
          showGrid={config.showGrid && !compact}
          showAxes={config.showAxes && !compact}
          // The value axis drops out on a short card, but the category axis is
          // what NAMES each bar — without it the card is anonymous stripes. Only
          // the user's own Axes toggle turns it off.
          showCategoryAxis={config.showAxes}
          categoryTickFormatter={(value) => truncateLabel(value, compact ? 10 : 20)}
          valueTickFormatter={measure.compact}
          ariaLabel={`${measure.label} by group`}
          renderTooltip={(row) => (
            <WidgetChartTooltip
              label={row.label}
              details={
                several ? (
                  // Every field's figure for the category, in the bars' hues.
                  <ChartLegend
                    direction="column"
                    items={measures.map((format, index) => ({
                      label: format.label,
                      colorToken: seriesColors.tokens[index],
                      value: format.format(row[seriesKey(index)] ?? 0),
                    }))}
                  />
                ) : (
                  row.valueLabel
                )
              }
              // Under a field measure the projects behind the figure are the
              // second fact a reader wants; under the count they ARE the figure.
              footer={measure.measure ? measure.projects(row.count) : undefined}
            />
          )}
          onBarClick={onItemClick}
        />
      </WidgetInteractive>
      {showLegend ? (
        <ChartLegend
          items={measures.map((format, index) => ({ label: format.label, colorToken: seriesColors.tokens[index] }))}
        />
      ) : null}
      {folded > 0 ? (
        <Text size="xs" variant="muted" truncate>
          {`${folded.toLocaleString()} ${folded === 1 ? "other" : "others"}${additive ? ` · ${shareLabel(hiddenValue, total, measure)}` : ""}`}
        </Text>
      ) : null}
    </Box>
  );
}

const dimensionBarSettings = (config: DimensionBarConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  // The Data section splits by axis ROLE: what the bars are (the category
  // axis — which groups, how many, in what order, whether the no-value group
  // shows) and what they measure (the value axis — the figure, its aggregate,
  // the fields compared beside it). Named for the roles, not X and Y: the bars
  // run as rows, so the category axis is the vertical one here.
  {
    title: "Bars",
    fields: [
      groupBySettingField(),
      { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
      {
        key: "expandedShow",
        kind: "select",
        label: "Show expanded",
        icon: "maximize-2",
        options: SLICE_SHOW_OPTIONS,
      },
      // The bar has two Show budgets, so it lays its section out itself and
      // takes only the Sort by row from the shared shape set.
      ...SLICE_SHAPE_FIELDS.filter((field) => field.key === "sort"),
      SHOW_UNASSIGNED_SETTING_FIELD,
    ],
  },
  {
    title: "Values",
    fields: measureSettingFields(MEASURE_SETTING_FIELD, measureFieldId(config.measure) !== null, true),
  },
  {
    title: "Display",
    fields: [...AXES_DISPLAY_FIELDS],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const dimensionBarDef: WidgetModule<DimensionBarConfig> = {
  type: "dimension-bar",
  title: "Bar chart",
  dynamicTitle: (config, context) => workloadTitle(config, config.groupBy, "Bar chart", context),
  icon: "bar-chart",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: dimensionBarConfigSchema,
  settings: dimensionBarSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "dimension-bar.custom",
      label: "Bar chart",
      description: "Rank groups by Project count or a field total — by type, stage, team, assignee, follow up, or a custom field.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
    },
    {
      key: "dimension-bar.assignee",
      label: "Workload by assignee",
      description: "Rank Project counts by assignee.",
      section: "members" satisfies WidgetSection,
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee" },
      locks: ["groupBy"],
      defaultW: 5,
      autoTitle: false,
    },
    {
      key: "dimension-bar.leaderboard",
      label: "Leaderboard",
      description: "Rank members by Project volume and turn ownership into a competition.",
      section: "members" satisfies WidgetSection,
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee", sort: "volume", show: "top12" },
      locks: ["groupBy"],
      defaultW: 5,
    },
    {
      key: "dimension-bar.workflow",
      label: "Workload by workflow",
      description: "Rank Project counts across every workflow in scope.",
      section: "workflows" satisfies WidgetSection,
      tags: ["projects", "workflows"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "workflow" },
      locks: ["groupBy"],
      defaultW: 5,
      // The label matches the derived title, so stay untitled and let
      // `dynamicTitle` track `groupBy` as the user regroups.
      autoTitle: false,
    },
    {
      key: "dimension-bar.outcome",
      label: "Top outcomes",
      description: "Rank the outcomes members are recording on their Project Updates.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["activity", "outcomes", "members"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      // Counts UPDATES, not projects — so the label sits outside the derived
      // "Workload by <dimension>" family on purpose and bakes as a real title.
      defaultConfig: { groupBy: "outcome", sort: "volume", show: "top8" },
      locks: ["groupBy"],
      defaultW: 5,
    },
    {
      key: "dimension-bar.new-by-assignee",
      label: "New Projects by assignee",
      description:
        "Projects created this week, per assignee, biggest first.",
      section: "members" satisfies WidgetSection,
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      icon: "sparkles",
      // A curated name outside the derived family: it sticks like a rename.
      defaultConfig: { groupBy: "assignee", sort: "volume", show: "top12", filters: { dateRange: { key: "this-week" } } },
      locks: ["groupBy"],
      defaultW: 5,
    },
  ],
  render: DimensionBarRender,
};
